import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

function formatPhone(val) {
  const digits = val.replace(/[^0-9]/g, '').replace(/^1/, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return '+1 (' + digits;
  if (digits.length <= 6) return '+1 (' + digits.slice(0,3) + ') ' + digits.slice(3);
  return '+1 (' + digits.slice(0,3) + ') ' + digits.slice(3,6) + '-' + digits.slice(6);
}

export default function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const defaultPlan = location.state?.plan || new URLSearchParams(location.search).get('plan') || 'Pro';
  const sessionId = new URLSearchParams(location.search).get('session_id');
  const prefillEmail = new URLSearchParams(location.search).get('email') || '';
  const prefillName = new URLSearchParams(location.search).get('name') || '';

  const savedForm = sessionId ? JSON.parse(sessionStorage.getItem('flo_signup') || '{}') : {};

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: savedForm.name || decodeURIComponent(prefillName),
    email: savedForm.email || decodeURIComponent(prefillEmail),
    password: savedForm.password || '',
    plan: savedForm.plan || defaultPlan,
    agency: savedForm.agency || '',
    phone: savedForm.phone || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCreate = async (formData) => {
    const data = formData || form;
    setLoading(true);
    setError('');
    try {
      const res = await api.signup(data);
      sessionStorage.removeItem('flo_signup');
      login({ ...res.user, token: res.token });
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId && savedForm.password) {
      handleCreate(savedForm);
    }
  }, []);

  const handleSelectPlan = async (planName) => {
    if (!form.name || !form.email || !form.password) {
      setError('Please complete step 1 first.');
      setStep(1);
      return;
    }
    const formWithPlan = { ...form, plan: planName };
    setLoading(true);
    setError('');
    try {
      if (planName === 'Basic') {
        const res = await api.signup(formWithPlan);
        sessionStorage.removeItem('flo_signup');
        login({ ...res.user, token: res.token });
        navigate('/dashboard');
      } else {
        sessionStorage.setItem('flo_signup', JSON.stringify(formWithPlan));
        const base = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
        const res = await fetch(base + '/billing/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan: planName, email: form.email, name: form.name }),
        });
        const data = await res.json();
        if (data.url) window.location.href = data.url;
        else { setError(data.error || 'Could not redirect to payment'); setLoading(false); }
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const plans = [
    { name: "Basic", price: "Free",    color: "#185FA5", desc: "Full AI + CRM. No card required ever.", cta: "Start for free" },
    { name: "Pro",   price: "$200/mo", color: "#534AB7", desc: "Everything in Free + basic lead scraper access.", cta: "Start 14-day trial" },
    { name: "Pro+",  price: "$397/mo", color: "#0F6E56", desc: "Everything in Pro + pro lead scraper access.", cta: "Start 14-day trial" },
  ];

  if (sessionId && savedForm.password && loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#0e0e0c", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
        <div style={{ width: 36, height: 36, borderRadius: 9, background: "linear-gradient(135deg,#7F77DD,#1D9E75)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>F</span>
        </div>
        <div style={{ fontSize: 16, color: "#f0efe9", fontWeight: 500 }}>Setting up your FLO account...</div>
        <div style={{ fontSize: 13, color: "rgba(240,239,233,0.4)" }}>Payment confirmed — just a moment</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0e0e0c", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <button onClick={() => navigate('/')} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", marginBottom: 36 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg,#7F77DD,#1D9E75)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 17 }}>F</span>
        </div>
        <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.8px", color: "#f0efe9" }}>FLO</span>
      </button>

      <div style={{ width: "100%", maxWidth: 460, background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: "36px 32px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 28 }}>
          {[1, 2].map(s => (
            <React.Fragment key={s}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: step >= s ? "#534AB7" : "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: step >= s ? "#fff" : "rgba(240,239,233,0.3)", flexShrink: 0 }}>{s}</div>
              {s < 2 && <div style={{ flex: 1, height: 1, background: step > s ? "#534AB7" : "rgba(255,255,255,0.08)" }} />}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && <>
          <h1 style={{ fontSize: 21, fontWeight: 700, color: "#f0efe9", marginBottom: 6, letterSpacing: "-0.5px" }}>Create your account</h1>
          <p style={{ fontSize: 13, color: "rgba(240,239,233,0.45)", marginBottom: 24 }}>14-day free trial — no card required to start</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            {[
              { k: "name",     label: "Full name",              placeholder: "James Smith",           type: "text" },
              { k: "agency",   label: "Agency / business name", placeholder: "Smith Insurance Agency", type: "text" },
              { k: "email",    label: "Email",                  placeholder: "james@agencymail.com",  type: "email" },
              { k: "phone",    label: "Phone number",           placeholder: "+1 (813) 555-0100",     type: "tel" },
              { k: "password", label: "Password",               placeholder: "Min. 8 characters",     type: "password" },
            ].map(f => (
              <div key={f.k}>
                <div style={{ fontSize: 11, fontWeight: 500, color: "rgba(240,239,233,0.5)", marginBottom: 5 }}>{f.label}</div>
                <input value={form[f.k]} onChange={e => set(f.k, f.k === 'phone' ? formatPhone(e.target.value) : e.target.value)} type={f.type} placeholder={f.placeholder} style={{ width: "100%", padding: "9px 13px", borderRadius: 8, border: "0.5px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", fontSize: 13, color: "#f0efe9", outline: "none", boxSizing: "border-box" }} />
              </div>
            ))}
          </div>
          {error && <div style={{ marginTop: 12, fontSize: 12, color: "#F09595", padding: "8px 12px", borderRadius: 7, background: "rgba(226,75,74,0.1)", border: "0.5px solid rgba(226,75,74,0.2)" }}>{error}</div>}
          <button onClick={() => setStep(2)} style={{ marginTop: 20, width: "100%", padding: "11px", borderRadius: 9, border: "none", background: "#534AB7", color: "#fff", fontSize: 14, cursor: "pointer", fontWeight: 600 }}>Continue →</button>
          <div style={{ marginTop: 16, textAlign: "center", fontSize: 12, color: "rgba(240,239,233,0.35)" }}>
            Already have an account?{" "}
            <button onClick={() => navigate('/login')} style={{ background: "none", border: "none", color: "#AFA9EC", cursor: "pointer", fontSize: 12, fontWeight: 500 }}>Sign in</button>
          </div>
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "0.5px solid rgba(255,255,255,0.07)", display: "flex", gap: 16, justifyContent: "center" }}>
            <button onClick={() => navigate('/privacy')} style={{ background: "none", border: "none", color: "rgba(240,239,233,0.3)", cursor: "pointer", fontSize: 11 }}>Privacy Policy</button>
            <button onClick={() => navigate('/terms')} style={{ background: "none", border: "none", color: "rgba(240,239,233,0.3)", cursor: "pointer", fontSize: 11 }}>Terms of Service</button>
          </div>
        </>}

        {step === 2 && <>
          <h1 style={{ fontSize: 21, fontWeight: 700, color: "#f0efe9", marginBottom: 6, letterSpacing: "-0.5px" }}>Choose your plan</h1>
          <p style={{ fontSize: 13, color: "rgba(240,239,233,0.45)", marginBottom: 24 }}>Free forever on Basic. Pro and Pro+ include a 14-day trial.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
            {plans.map(p => (
              <div key={p.name} style={{ borderRadius: 10, border: "0.5px solid " + p.color + "40", background: p.color + "10", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#f0efe9" }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: "rgba(240,239,233,0.4)", marginTop: 3, maxWidth: 220 }}>{p.desc}</div>
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#f0efe9", flexShrink: 0, marginLeft: 8 }}>{p.price}</span>
                </div>
                <button onClick={() => handleSelectPlan(p.name)} disabled={loading} style={{ padding: "10px", borderRadius: 8, border: "none", background: p.color, color: "#fff", fontSize: 13, cursor: "pointer", fontWeight: 600, opacity: loading ? 0.7 : 1, width: "100%", fontFamily: "inherit" }}>
                  {loading ? "Processing..." : p.cta}
                </button>
              </div>
            ))}
          </div>
          {error && <div style={{ marginBottom: 12, fontSize: 12, color: "#F09595", padding: "8px 12px", borderRadius: 7, background: "rgba(226,75,74,0.1)", border: "0.5px solid rgba(226,75,74,0.2)" }}>{error}</div>}
          <button onClick={() => setStep(1)} style={{ padding: "9px 16px", borderRadius: 9, border: "0.5px solid rgba(255,255,255,0.12)", background: "transparent", color: "rgba(240,239,233,0.5)", fontSize: 13, cursor: "pointer", width: "100%", fontFamily: "inherit" }}>← Back</button>
        </>}
      </div>
    </div>
  );
}